import { Spin } from "antd";
import type { PropsWithChildren } from "react";
import { defineMessages, useIntl } from "react-intl";

import { LocalityForm } from "../../components/agro/localities/LocalityForm";
import { Form } from "../../components/form/Form";
import { BottomPanel } from "../../components/shared/BottomPanel";
import BasicLayout from "../../layouts/BasicLayout";
import { evictCache } from "../../lib/apollo/utils";
import {
  type LocalityFields,
  type LocalityFragment,
  UnitType,
} from "../../lib/graphql/generated";
import { useLocalityCreate } from "../../lib/hooks/basic/localities";
import { useUnitOptions } from "../../lib/hooks/basic/units";
import { useCurrentUser } from "../../lib/hooks/basic/users";
import routes, { routerPush } from "../../lib/routes";

const messages = defineMessages({
  new: { id: "new.header", defaultMessage: "new" },
  locality: {
    id: "localities.entityName",
    defaultMessage: "locality",
  },
});

export default function New() {
  const intl = useIntl();
  const entityName = intl.formatMessage(messages.locality);

  return (
    <BasicLayout
      title={intl.formatMessage(messages.new, { entityName })}
      breadcrumbs={[
        { path: routes.localities.index, breadcrumbName: entityName },
        {
          path: routes.localities.new,
          breadcrumbName: intl.formatMessage({ id: "new" }),
        },
      ]}
      authorize={(p) => !!p?.locality?.write}
    >
      <NewLocalityForm
        afterSubmit={(locality: LocalityFragment) => {
          routerPush(locality.id, routes.localities.details);
        }}
      >
        <BottomPanel
          buttons={[
            BottomPanel.CancelButton({
              route: routes.localities.index,
            }),
            BottomPanel.SubmitButton(),
          ]}
        />
      </NewLocalityForm>
    </BasicLayout>
  );
}

export function NewLocalityForm({
  children,
  afterSubmit,
}: PropsWithChildren<{
  afterSubmit?: (locality: LocalityFragment) => void;
}>) {
  const { currentTenant } = useCurrentUser();

  const [create] = useLocalityCreate({
    update: evictCache("localities"),
    refetchQueries: ["Me"],
  });

  const { unitOptions } = useUnitOptions({
    types: [UnitType.Area],
  });

  if (!unitOptions.length)
    return (
      <div style={{ textAlign: "center" }}>
        <Spin />
      </div>
    );

  const areaUnitId = unitOptions.find(
    (u) => u.unit.abbr === (currentTenant.address?.country?.areaUnit || "ha")
  )?.key;

  return (
    <Form
      preventLeaving
      layout="vertical"
      initialValues={{ areaUnitId } as LocalityFields}
      onSubmit={(locality, { setSubmitting, showErrors }) => {
        create({
          variables: {
            fields: {
              name: locality.name,
              internalId: locality.internalId,
              geoJson: locality.geoJson,
              areaUnitId: locality.areaUnitId,
              totalArea: locality.totalArea,
            },
          },
          refetchQueries: ["Me"],
        }).then((f) => {
          if (!f.data?.localityCreate) return;

          if (f.data?.localityCreate?.result) {
            if (afterSubmit) afterSubmit(f.data.localityCreate.result);
          } else if (f.errors) {
            showErrors(f.data.localityCreate.errors);
            setSubmitting(false);
          }
        });
      }}
    >
      <LocalityForm unitOptions={unitOptions} />

      {children}
    </Form>
  );
}
